import { decamelizeKeys } from "humps";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CartProduct,
  DBProduct,
  useAppNavigate,
  useAnalytics,
  useInventory,
  ProductType,
  Topping,
  getDbProductId,
} from "@commons-monorepo/index";
import { ProductSelectorProps } from "./product-selector.types";
import {
  getVariantByToppings,
  priceCalculator,
  priceWithWholesale,
} from "@commons-monorepo/index";
import { productSelectorEvents } from "./events";

export const useProductSelector = ({
  product,
  offerId,
  selectedToppings,
  onRequiredTopping,
  cartProducts,
  addProduct,
  theme,
  storeMetadata,
}: ProductSelectorProps) => {
  const { t } = useTranslation();
  const [logEvent] = useAnalytics({ storeMetadata });
  const { canAddProduct, hasProductTypeInTheCart } = useInventory({
    cartProducts,
  });
  const navigate = useAppNavigate();
  const payloadTheme = decamelizeKeys({
    templateId: theme.templateId,
    headerId: theme.template.HEADER,
    bannerId: theme.template.COVER,
    categoriesId: theme.template.CATEGORIES,
    cardId: theme.template.PRODUCT_CARD,
    footerId: theme.template.FOOTER,
    storeInfoId: theme.template.STORE_INFO.SKIN,
  });

  const logEventFn = (eventProps: any) => {
    logEvent(eventProps.key, eventProps.payload, eventProps.providers);
  };

  const allRequiredToppings = useMemo(() => product.toppings || [], [product]);

  const numberRequiredToppings = useMemo(
    () => allRequiredToppings.length,
    [product],
  );

  const requiredToppings = useMemo(() => {
    return (
      product.toppings?.filter(
        (topping: Topping) =>
          selectedToppings?.find((item) => topping.name === item.toppingName),
      ) || []
    );
  }, [product, selectedToppings]);

  const hasDisabled = useMemo(() => {
    return (
      (product?.toppings &&
        requiredToppings?.length < numberRequiredToppings) ||
      !canAddProduct(product, selectedToppings)
    );
  }, [product, selectedToppings, requiredToppings]);

  const productDbId = useMemo(() => {
    const variant = getVariantByToppings(
      product.productToppingVariations || [],
      selectedToppings || [],
    );
    const payload: DBProduct = {
      offerId,
      id: product.id,
      quantity: 1,
      toppings: selectedToppings || [],
      variationId: variant?.id,
      price: priceCalculator(product, selectedToppings),
      wholesalePrice: priceWithWholesale(product, selectedToppings),
      wholesale: product.wholesale,
    };
    return getDbProductId(payload);
  }, [selectedToppings, product]);

  const hasProductInTheCart = useMemo((): boolean => {
    return !!cartProducts.find((p) => p.dbId === productDbId);
  }, [cartProducts, product, selectedToppings]);

  const handleAddProduct = (productId: string) => {
    if (product.type === ProductType.Service) {
      const hasAddedService = !!cartProducts.find((cp) => cp.id === product.id);
      if (hasAddedService) return;
    }

    let variationId;
    if (product.productToppingVariations) {
      const variant = getVariantByToppings(
        product.productToppingVariations,
        selectedToppings || [],
      );
      variationId = variant?.id;
    }

    const payload: CartProduct = {
      id: productId,
      quantity: 1,
      toppings: selectedToppings || [],
      price: priceCalculator(product, selectedToppings),
      wholesalePrice: priceWithWholesale(product, selectedToppings),
      wholesale: product.wholesale,
      type: product.type,
      variationId,
    } as CartProduct;
    if (offerId) {
      payload.offerId = offerId;
    }

    addProduct(payload);
  };

  const handleClickButton = () => {
    if (hasDisabled) {
      onRequiredTopping && onRequiredTopping();
    }
  };

  const handleBuyButtonClick = () => {
    if (!hasDisabled) {
      if (product.type === ProductType.Service) {
        if (
          hasProductTypeInTheCart(ProductType.Service) ||
          hasProductTypeInTheCart(ProductType.Product) ||
          hasProductTypeInTheCart(undefined)
        ) {
          return;
        }
        logEventFn({
          key: productSelectorEvents.SELECT_ADD_PRODUCT,
          payload: { ...payloadTheme },
          providers: ["clevertap", "facebook", "appsflyer"],
        });
        const toppingsParams = (selectedToppings || [])
          .map((topping) => `${topping.toppingName}=${topping.value}`)
          .join("&");
        navigate(
          `agregar-servicio${toppingsParams ? `?${toppingsParams}` : ""}`,
        );
        return;
      } else if (
        product.type === ProductType.Product ||
        product.type === undefined
      ) {
      }

      handleAddProduct(product.id);
      logEventFn({
        key: productSelectorEvents.SELECT_ADD_PRODUCT,
        payload: { ...payloadTheme },
        providers: ["clevertap", "facebook", "appsflyer"],
      });
    } else {
      handleClickButton();
    }
  };

  return { t, hasDisabled, hasProductInTheCart, handleBuyButtonClick };
};
